import { ReactElement, useState } from "react";

import styles from "../../InternationalSolutionsPage.module.css";
import { Accordion, AccordionPanel } from "@justworkshr/milo-core";
import { oktaAuth, pkceIsEnabled } from "app/okta";
import {
  useGetCompanyDisplayQuery,
  useGetIsEmployerOfRecordEnabledQuery,
} from "types/generated/operations";
import {
  CUSTOMER_IO_CTA_CLICK_EVENT,
  sendCustomerIOEvent,
} from "app/customer-io";
import { FLAG_NAMES, flagIsEnabled } from "lib/environment";
import { CF_ADD_EMPLOYEES_LINK } from "pages/international-solutions/constants";
import ThankYouDialog from "../Dialog/ThankYouDialog";

export const EORPromoFAQ = (): ReactElement => {
  const { componentWrapper, mdHead, accordionOverride } = styles;

  const [showThankYouDialog, setShowThankYouDialog] = useState(false);
  const handleThankYouDialog = () => {
    setShowThankYouDialog((prev) => !prev);
  };

  const { data: eorEnabledData } = useGetIsEmployerOfRecordEnabledQuery();
  const isEmployerOfRecordEnabled =
    eorEnabledData?.authenticatedMember.company.companySetting?.value ===
      "true" && flagIsEnabled(FLAG_NAMES.EMPLOYER_OF_RECORD);

  const accessToken = pkceIsEnabled() ? oktaAuth.getAccessToken() : "";
  const { data: memberData } = useGetCompanyDisplayQuery();

  const triggerCustomerIOEvent = (ctaName: string, nextUrl: string) => {
    sendCustomerIOEvent(
      {
        event_type: CUSTOMER_IO_CTA_CLICK_EVENT,
        member_uuids: [memberData?.authenticatedMember.uuid ?? ""],
        company_uuids: [memberData?.authenticatedMember.company.uuid ?? ""],
        data: {
          current_url: window.location.href,
          cta_name: ctaName,
          next_url: nextUrl,
        },
      },
      accessToken
    );
  };

  const handleCTAClick = () => {
    if (isEmployerOfRecordEnabled) {
      triggerCustomerIOEvent(
        "eor_eor_promo_add_employee_button",
        CF_ADD_EMPLOYEES_LINK
      );
      window.location.href = CF_ADD_EMPLOYEES_LINK;
    } else {
      triggerCustomerIOEvent(
        "eor_eor_promo_im_interested_button",
        "thank-you-modal"
      );
      handleThankYouDialog();
    }
  };

  return (
    <div className={componentWrapper}>
      <div className={mdHead}>FAQs</div>
      <div className={`${componentWrapper} ${accordionOverride}`}>
        <Accordion>
          <AccordionPanel
            isOpen
            title="How do I hire an international employee through Justworks using the discount?"
          >
            <span>
              <a
                style={{ cursor: "pointer" }}
                data-heap-id={
                  isEmployerOfRecordEnabled
                    ? "eor-add-employees-button"
                    : "no-eor-im-interested-button"
                }
                onClick={handleCTAClick}
              >
                Connect with our team to get started.
              </a>{" "}
              Please note that you must be an active Justworks PEO customer,
              using EOR services for the first time, when enrolling in
              Justworks' Employer of Record solution in order to unlock the
              discount pricing.
            </span>
          </AccordionPanel>
          <AccordionPanel isOpen title="How long does the discount period run?">
            Discount pricing runs for 12 consecutive months, beginning the first
            month you pay your international employee through Justworks'
            Employer of Record (EOR) solution. For example, if you hire your
            first international employee in May 2025, you will be billed the
            discounted rate of $299 per month through April 2026.
          </AccordionPanel>
          <AccordionPanel
            isOpen
            title="What is EOR pricing after the discount period ends?"
          >
            After the discount period ends, you will be billed $599 per employee
            per month (PEPM).
          </AccordionPanel>
          <AccordionPanel
            isOpen
            title="Is there a cap on the number of employees that can get this discount?"
          >
            There is no cap on the number of EOR employees eligible for the
            promotion.
          </AccordionPanel>

          <AccordionPanel
            isOpen
            title="If I hire international employees in different countries during the 12-month period, does the discount apply to all PEPM fees?"
          >
            Yes, the discount applies to any international employee PEPM fee
            during the discount period, in any country outside of U.S.. For
            example, if you hire an employee in India and an employee in Spain,
            both employee fees would be $299, respectively, during the discount
            period.
          </AccordionPanel>

          <AccordionPanel
            isOpen
            title="Can this discount be applied to international contractors?"
          >
            This promo doesn't apply to international contractors, but employee
            conversions qualify. Make sure you take advantage of this
            limited-time discount if you've been considering converting
            international contractors to full-time employees.
          </AccordionPanel>
        </Accordion>
      </div>
      {showThankYouDialog && (
        <ThankYouDialog
          isOpen={showThankYouDialog}
          onClose={handleThankYouDialog}
        />
      )}
    </div>
  );
};
